/**
 * 💰 Cassandra & Ledger Money Egg — Crack Reveal Modal (Dragonious canon · Feb 2026)
 *
 * Presents the player with a shakable/crackable egg and reveals the RNG drop
 * with a staged animation:
 *   1. Idle egg (pink/blue/black tri-color) with pulse
 *   2. Player taps CRACK — shake animation kicks in, POST /crack fires
 *   3. On response: crack split lines, then coin/emoji rain reveal card
 *
 * Backed by:
 *   POST /api/currency-vouchers/money-egg/crack   →  { roll: {currency, amount, tier, credit_note, flavor}, inventory }
 *
 * Excluded from RNG: Bird Crackers, Inner Light (per owner canon).
 * Included:          Chits, Battle Coins, Astral Coins, Treasure Keys, Glimmer Pieces, self-echo Egg.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency → { icon, label, accent }
const CURRENCY_META = {
  chits:          { icon: "🪙", label: "Chits",          accent: "text-amber-300",  glow: "shadow-amber-500/50"  },
  battle_coins:   { icon: "🎫", label: "Battle Coins",   accent: "text-red-300",    glow: "shadow-red-500/50"    },
  astral_coins:   { icon: "🌠", label: "Astral Coins",   accent: "text-violet-300", glow: "shadow-violet-500/50" },
  treasure_keys:  { icon: "🗝️", label: "Treasure Keys",  accent: "text-yellow-200", glow: "shadow-yellow-500/50" },
  glimmer_pieces: { icon: "✨", label: "Glimmer Pieces", accent: "text-sky-300",    glow: "shadow-sky-500/50"    },
  cl_money_egg:   { icon: "💰", label: "Money Eggs",     accent: "text-pink-300",   glow: "shadow-pink-500/50"   },
};

const TIER_META = {
  common:    { label: "COMMON",    color: "text-neutral-300", ring: "ring-neutral-500/50",  chip: "bg-neutral-800"                  },
  epic:      { label: "EPIC",      color: "text-indigo-300",  ring: "ring-indigo-500/60",   chip: "bg-indigo-900/70"                },
  rare:      { label: "RARE",      color: "text-sky-300",     ring: "ring-sky-500/60",      chip: "bg-sky-900/70"                   },
  legendary: { label: "LEGENDARY", color: "text-amber-300",   ring: "ring-amber-500/70",    chip: "bg-amber-900/70"                 },
  mythic:    { label: "MYTHIC",    color: "text-pink-300",    ring: "ring-pink-500/80",     chip: "bg-gradient-to-r from-pink-800/80 to-fuchsia-900/80" },
};

// Confetti chars per tier
const CONFETTI = {
  common:    ["🪙", "·", "•"],
  epic:      ["🪙", "💠", "✦"],
  rare:      ["✨", "💠", "🪙"],
  legendary: ["🌟", "💰", "🗝️", "🪙"],
  mythic:    ["💥", "🌠", "🎰", "💰", "✨"],
};

export default function MoneyEggCrackModal({ open, onClose, ownedCount = 0, onCrackComplete }) {
  const [phase, setPhase] = useState("idle");        // idle | shaking | revealed
  const [roll, setRoll] = useState(null);            // {currency, amount, tier, credit_note, flavor}
  const [remaining, setRemaining] = useState(ownedCount);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);        // user's recent cracks
  const [bigDrops, setBigDrops] = useState([]);      // global mythic+legendary ticker

  // Sync remaining when opened. NOTE: `ownedCount` intentionally NOT in the
  // dep array — otherwise the parent's inventory update (fired by our own
  // successful crack via `onCrackComplete`) would reset phase→"idle" and
  // wipe the reveal state before the user ever sees it. Remaining is kept
  // in sync inside `doCrack` via `setRemaining(nextRemaining)`.
  useEffect(() => {
    if (open) {
      setRemaining(ownedCount);
      setPhase("idle");
      setRoll(null);
      setError(null);
      // Load history + big drops feeds on open
      loadFeeds();
    }
  }, [open]);

  const loadFeeds = async () => {
    try {
      const [h, g] = await Promise.all([
        axios.get(`${API}/currency-vouchers/money-egg/history?limit=5`),
        axios.get(`${API}/currency-vouchers/money-egg/big-drops?limit=8`),
      ]);
      setHistory(h.data?.history || []);
      setBigDrops(g.data?.big_drops || []);
    } catch (_) { /* silent — feeds are nice-to-have */ }
  };

  const doCrack = async () => {
    if (phase === "shaking") return;
    if (remaining <= 0) {
      setError("No Money Eggs left in your satchel.");
      return;
    }
    setError(null);
    setPhase("shaking");
    setRoll(null);

    // Fire request + minimum shake duration in parallel so animation always feels intentional.
    const [resp] = await Promise.all([
      axios.post(`${API}/currency-vouchers/money-egg/crack`).catch((e) => ({ error: e })),
      new Promise((r) => setTimeout(r, 1400)),
    ]);

    if (resp?.error) {
      setPhase("idle");
      const msg = resp.error?.response?.data?.detail || "Crack failed — the shell held.";
      setError(msg);
      toast.error(msg);
      return;
    }

    const data = resp?.data || {};
    const rolled = data.roll || {};
    setRoll(rolled);
    const nextRemaining = Number(data?.inventory?.cl_money_egg ?? 0);
    setRemaining(nextRemaining);
    setPhase("revealed");

    // Fire toast in addition to modal reveal for consistency w/ other rewards
    toast.success(rolled.credit_note || "Money Egg cracked!", { duration: 4500 });

    if (typeof onCrackComplete === "function") {
      try { onCrackComplete(data); } catch (_) { /* noop */ }
    }

    // Refresh recent + big drops feeds after each successful crack
    loadFeeds();
  };

  if (!open) return null;

  const tier = TIER_META[roll?.tier] || TIER_META.common;
  const curr = roll ? (CURRENCY_META[roll.currency] || { icon: "🪙", label: roll.currency, accent: "text-white", glow: "" }) : null;
  const confettiChars = CONFETTI[roll?.tier] || CONFETTI.common;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="money-egg-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && phase !== "shaking") onClose?.(); }}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl border-2 ${roll ? tier.ring : "ring-pink-500/40"} border-pink-500/40 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black shadow-2xl shadow-pink-500/20 p-6`}
        data-testid="money-egg-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          disabled={phase === "shaking"}
          data-testid="money-egg-close-btn"
          className="absolute top-3 right-3 text-neutral-500 hover:text-white text-xl disabled:opacity-30"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header */}
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Dragonious · Item Vendor</div>
          <div className="text-lg font-black text-pink-300 mt-1" style={{ fontFamily: "Anton, sans-serif" }}>
            CASSANDRA &amp; LEDGER MONEY EGG
          </div>
          <div className="text-[10px] text-neutral-400 italic mt-1">
            Random currency drop · 1 – 250 · weighted RNG
          </div>
        </div>

        {/* Egg / Reveal Stage */}
        <div className="relative h-64 flex items-center justify-center overflow-hidden rounded-xl bg-black/40 border border-pink-500/20 mb-4">
          {phase !== "revealed" && (
            <div
              className={`text-[9rem] leading-none select-none ${
                phase === "shaking" ? "animate-egg-shake" : "animate-egg-pulse"
              }`}
              data-testid="money-egg-visual"
              aria-hidden
            >
              💰
            </div>
          )}

          {phase === "revealed" && roll && (
            <div className="w-full text-center relative animate-reveal-pop" data-testid="money-egg-reveal">
              {/* Confetti burst */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {Array.from({ length: 14 }).map((_, i) => (
                  <span
                    key={i}
                    className="absolute text-2xl opacity-0 animate-confetti"
                    style={{
                      left: `${(i * 7) % 100}%`,
                      top: `${(i * 13) % 90}%`,
                      animationDelay: `${(i * 60) % 900}ms`,
                    }}
                  >
                    {confettiChars[i % confettiChars.length]}
                  </span>
                ))}
              </div>

              <div className={`text-[7rem] leading-none mb-1 drop-shadow-[0_0_24px_rgba(255,105,180,0.6)] ${curr.glow}`}>
                {curr.icon}
              </div>
              <div className={`inline-block px-3 py-0.5 rounded-full text-[10px] font-black tracking-widest ${tier.chip} ${tier.color} ring-1 ${tier.ring}`}>
                {tier.label}
              </div>
              <div className="mt-2 text-4xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }} data-testid="money-egg-reveal-amount">
                +{Number(roll.amount).toLocaleString()}
              </div>
              <div className={`text-sm font-bold ${curr.accent} uppercase tracking-wide`} data-testid="money-egg-reveal-currency">
                {curr.label}
              </div>
              {roll.currency === "cl_money_egg" && (
                <div className="mt-2 text-[11px] text-pink-300 italic">
                  🎲 Self-echo! The spreadsheet folds back on itself.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Flavor / meta */}
        {phase === "idle" && (
          <div className="text-[11px] text-neutral-400 italic text-center leading-snug mb-4">
            &ldquo;Cassandra audits every book, Ledger every crumb. Crack the egg and see how the spreadsheet lands today.&rdquo;
            <div className="text-[10px] text-neutral-600 mt-1">— Cassandra Gutkowski 🐦📓 &amp; Ledger</div>
          </div>
        )}

        {phase === "shaking" && (
          <div className="text-[11px] text-pink-300 text-center italic mb-4 animate-pulse">
            The shell is trembling…
          </div>
        )}

        {error && (
          <div className="text-[11px] text-red-400 text-center mb-3" data-testid="money-egg-error">
            {error}
          </div>
        )}

        {/* Counter */}
        <div className="flex items-center justify-between text-xs text-neutral-400 mb-3 px-1">
          <div>
            In satchel: <span className="text-pink-300 font-black" data-testid="money-egg-remaining">{remaining}</span>
          </div>
          <div className="text-[10px] italic text-neutral-500">
            🚫 No Bird Crackers · No Inner Light
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {phase !== "revealed" ? (
            <button
              onClick={doCrack}
              disabled={phase === "shaking" || remaining <= 0}
              data-testid="money-egg-crack-btn"
              className="flex-1 py-3 rounded-lg text-sm font-black tracking-widest transition
                bg-gradient-to-r from-pink-600 via-fuchsia-600 to-blue-700 hover:from-pink-500 hover:via-fuchsia-500 hover:to-blue-600 text-white
                disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-pink-500/40"
            >
              {phase === "shaking" ? "🥚 CRACKING…" : `💥 CRACK THE EGG`}
            </button>
          ) : (
            <>
              <button
                onClick={() => { setPhase("idle"); setRoll(null); }}
                disabled={remaining <= 0}
                data-testid="money-egg-again-btn"
                className="flex-1 py-3 rounded-lg text-xs font-black tracking-widest transition
                  bg-gradient-to-r from-pink-600 to-fuchsia-700 hover:from-pink-500 hover:to-fuchsia-600 text-white
                  disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {remaining > 0 ? "🎲 CRACK ANOTHER" : "🥚 OUT OF EGGS"}
              </button>
              <button
                onClick={onClose}
                data-testid="money-egg-done-btn"
                className="flex-1 py-3 rounded-lg text-xs font-black tracking-widest transition
                  bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-700"
              >
                DONE
              </button>
            </>
          )}
        </div>

        {/* 📜 Recent cracks (per-user) */}
        {history.length > 0 && (
          <div className="mt-5 pt-4 border-t border-neutral-800" data-testid="money-egg-history">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-2">
              📜 Your Recent Cracks
            </div>
            <div className="space-y-1">
              {history.slice(0, 5).map((row, i) => {
                const cm = CURRENCY_META[row.currency] || { icon: "🪙", label: row.currency, accent: "text-white" };
                const tm = TIER_META[row.tier] || TIER_META.common;
                return (
                  <div
                    key={i}
                    data-testid={`money-egg-history-row-${i}`}
                    className="flex items-center justify-between text-[11px] rounded px-2 py-1 bg-black/40 border border-neutral-900"
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="text-base leading-none">{cm.icon}</span>
                      <span className={`font-black ${cm.accent}`}>+{Number(row.amount).toLocaleString()}</span>
                      <span className="text-neutral-400">{cm.label}</span>
                    </span>
                    <span className={`text-[9px] uppercase tracking-widest font-black ${tm.color}`}>
                      {tm.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 🌟 Global Big Drops ticker */}
        {bigDrops.length > 0 && (
          <div className="mt-4 pt-3 border-t border-neutral-800" data-testid="money-egg-big-drops">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-widest text-amber-300">
                🌟 Big Drops · Global
              </div>
              <div className="text-[9px] text-neutral-600">Mythic + Legendary only</div>
            </div>
            <div className="relative overflow-hidden">
              <div className="flex gap-2 animate-ticker whitespace-nowrap py-1">
                {[...bigDrops, ...bigDrops].map((row, i) => {
                  const cm = CURRENCY_META[row.currency] || { icon: "🪙", label: row.currency, accent: "text-white" };
                  const tm = TIER_META[row.tier] || TIER_META.legendary;
                  return (
                    <div
                      key={i}
                      data-testid={`money-egg-big-drop-${i}`}
                      className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full border ${tm.ring} ${tm.chip} shrink-0`}
                    >
                      <span className="text-neutral-300">{row.handle}</span>
                      <span className="text-base leading-none">{cm.icon}</span>
                      <span className={`font-black ${cm.accent}`}>+{Number(row.amount).toLocaleString()}</span>
                      <span className={`font-black ${tm.color}`}>{tm.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes egg-pulse {
          0%, 100% { transform: scale(1);       filter: drop-shadow(0 0 12px rgba(236,72,153,0.35)); }
          50%      { transform: scale(1.05);    filter: drop-shadow(0 0 22px rgba(236,72,153,0.65)); }
        }
        .animate-egg-pulse { animation: egg-pulse 1.6s ease-in-out infinite; }

        @keyframes egg-shake {
          0%, 100% { transform: rotate(0deg)   scale(1);    }
          15%      { transform: rotate(-14deg) scale(1.06); }
          30%      { transform: rotate(14deg)  scale(1.06); }
          45%      { transform: rotate(-12deg) scale(1.09); }
          60%      { transform: rotate(12deg)  scale(1.09); }
          75%      { transform: rotate(-8deg)  scale(1.12); }
          90%      { transform: rotate(8deg)   scale(1.14); }
        }
        .animate-egg-shake { animation: egg-shake 0.35s ease-in-out infinite; filter: drop-shadow(0 0 20px rgba(236,72,153,0.85)); }

        @keyframes reveal-pop {
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        .animate-reveal-pop { animation: reveal-pop 0.45s cubic-bezier(0.34,1.56,0.64,1) both; }

        @keyframes confetti-fly {
          0%   { transform: translateY(0)     rotate(0deg);   opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translateY(-140px) rotate(360deg); opacity: 0; }
        }
        .animate-confetti { animation: confetti-fly 1.4s ease-out forwards; }

        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker { animation: ticker-scroll 22s linear infinite; }
        .animate-ticker:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
}
